import { useId } from 'react';
import { unitMoney } from '../ui';

/**
 * One sighting of BrickLink's price guide, as the chart takes it. The shape is written out here rather than
 * imported so this file stands on its own and does not depend on the API module.
 */
export interface TrendPoint {
  at: string;
  currency: string | null;
  min: number | null;
  avg: number | null;
  qtyAvg: number | null;
  max: number | null;
  quantity: number | null;
  lots: number | null;
}

/** What a series says about the part's market price. */
export interface PriceTrend {
  state: 'rising' | 'falling' | 'flat' | 'too_little';
  latestAvg: number | null;
  earliestAvg: number | null;
  change: number | null;
  changePct: number | null;
  points: number;
  currency: string | null;
  from: string | null;
  to: string | null;
  summary: string;
}

/** One colour, condition and guide: one line on the chart. */
export interface PriceHistorySeries {
  colorId: number;
  colorName: string;
  /** The colour's own hex, when the API knows it. The swatch is only drawn if it is there. */
  colorRgb?: string | null;
  condition: 'N' | 'U';
  guideType: 'sold' | 'stock';
  series: TrendPoint[];
  trend: PriceTrend;
}

export interface PriceHistoryResponse {
  itemId: number;
  region: string;
  series: PriceHistorySeries[];
}

// ---------------------------------------------------------------- geometry

const VIEW_W = 720;
const VIEW_H = 240;
const PAD = { left: 64, right: 16, top: 16, bottom: 34 };
const PLOT_W = VIEW_W - PAD.left - PAD.right;
const PLOT_H = VIEW_H - PAD.top - PAD.bottom;

const at = (p: TrendPoint): number => {
  const t = Date.parse(p.at);
  return Number.isFinite(t) ? t : 0;
};

const shortDate = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const longDate = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

/**
 * A round step for the grid: 1, 2, 2.5, 5 or 10 times a power of ten, rounded down so the grid never ends up
 * with only two lines on it.
 */
const niceStep = (span: number, target: number): number => {
  if (!(span > 0)) return 1;
  const raw = span / target;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const norm = raw / mag;
  const step = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10;
  return step * mag;
};

/** The runs of consecutive points that have a value, so a gap in the data is a gap in the line, not a guess. */
const runs = (values: (number | null)[]): number[][] => {
  const out: number[][] = [];
  let current: number[] = [];
  values.forEach((v, i) => {
    if (v === null) {
      if (current.length) out.push(current);
      current = [];
    } else {
      current.push(i);
    }
  });
  if (current.length) out.push(current);
  return out;
};

// ---------------------------------------------------------------- the chart

/**
 * The market's price for a part, drawn as a line.
 *
 * The average is the line that matters, so the scale is built around it. BrickLink's lowest and highest prices
 * are drawn as a faint band behind the line, but only when they fit on the scale without squashing the average
 * flat; where they do not, the average is left alone and the spread is left out rather than drawn misleadingly.
 *
 * Everything is plain SVG in a viewBox, so the chart scales with the card it sits in and the labels scale with
 * it rather than stretching.
 */
export function PriceHistoryChart({ points, currency, className }: { points: TrendPoint[]; currency?: string | null; className?: string }) {
  const rawId = useId();
  const clipId = `ph-clip-${rawId.replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const ccy = currency ?? 'GBP';

  const sorted = [...points].sort((a, b) => at(a) - at(b));
  const avgs = sorted.map((p) => p.avg);
  const qtyAvgs = sorted.map((p) => p.qtyAvg);

  // The average line is the one to read, so the scale is set by it.
  const base = [...avgs, ...qtyAvgs].filter((v): v is number => v !== null);
  // One sighting is a price, not a trend: there is no line to draw yet.
  if (sorted.length < 2) {
    return (
      <p className="text-sm text-slate-500">
        {sorted.length === 0
          ? 'No price has been seen for this part yet.'
          : 'Only one price has been seen so far, so there is no line to draw yet.'}
      </p>
    );
  }
  if (!base.length) {
    return <p className="text-sm text-slate-500">No price has been seen for this part yet.</p>;
  }

  const baseLo = Math.min(...base);
  const baseHi = Math.max(...base);
  // A part whose price never moves still needs a scale with some room on it, or the line is a dot on a line.
  const baseSpan = baseHi - baseLo || Math.max(Math.abs(baseHi) * 0.2, 0.02);
  const pad = baseSpan * 0.12;
  let lo = Math.max(0, baseLo - pad);
  let hi = baseHi + pad;

  // BrickLink's lowest and highest prices. They may stretch the scale, but not so far that the average is
  // squashed flat; where they will not fit, they are left out rather than drawn against a scale that hides them.
  const extremes = sorted.flatMap((p) => [p.min, p.max]).filter((v): v is number => v !== null);
  let showExtremes = extremes.length > 0;
  if (showExtremes) {
    const limit = baseSpan * 2.5;
    const trialLo = Math.min(lo, Math.max(Math.min(...extremes), baseLo - limit));
    const trialHi = Math.max(hi, Math.min(Math.max(...extremes), baseHi + limit));
    if (Math.max(...extremes) <= trialHi + 1e-9) {
      lo = trialLo;
      hi = trialHi;
    } else {
      showExtremes = false;
    }
  }
  if (hi <= lo) hi = lo + 0.01;

  // Times decide where a point sits on the x axis, so uneven nights are shown as uneven spacing.
  const t0 = at(sorted[0]!);
  const t1 = at(sorted[sorted.length - 1]!);
  const x = (i: number): number => {
    if (t1 === t0) return PAD.left + PLOT_W / 2;
    return PAD.left + ((at(sorted[i]!) - t0) / (t1 - t0)) * PLOT_W;
  };
  const y = (v: number): number => PAD.top + (1 - (v - lo) / (hi - lo)) * PLOT_H;

  const avgRuns = runs(avgs);
  const qtyRuns = runs(qtyAvgs);
  const minRuns = showExtremes ? runs(sorted.map((p) => p.min)) : [];
  const maxRuns = showExtremes ? runs(sorted.map((p) => p.max)) : [];
  const hasQty = qtyRuns.length > 0 && sorted.some((p) => p.qtyAvg !== null && p.qtyAvg !== p.avg);

  // Grid: a few round numbers across the range.
  const step = niceStep(hi - lo, 4);
  const ticks: number[] = [];
  for (let v = Math.ceil(lo / step) * step; v <= hi + step / 1000 && ticks.length < 8; v += step) ticks.push(Number(v.toFixed(6)));
  if (ticks.length < 2) ticks.push(lo, (lo + hi) / 2, hi);

  // Dates: up to four, each pinned to a real sighting nearest a third of the way along.
  const labelIndex: number[] = [];
  for (const f of [0, 1 / 3, 2 / 3, 1]) {
    const target = t0 + (t1 - t0) * f;
    let best = 0;
    let bestDistance = Infinity;
    sorted.forEach((p, i) => {
      const distance = Math.abs(at(p) - target);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = i;
      }
    });
    if (!labelIndex.includes(best)) labelIndex.push(best);
  }
  // The same day twice in a row is one label. The later one is kept, so the newest date always survives.
  const labels = labelIndex
    .map((i) => ({ i, text: shortDate(sorted[i]!.at) }))
    .filter((l, idx, all) => all.findLastIndex((o) => o.text === l.text) === idx);

  const path = (indices: number[], values: (number | null)[]): string =>
    indices.map((i, n) => `${n === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(values[i]!).toFixed(1)}`).join(' ');

  // The band between the lowest and highest price, where both are known.
  const band = showExtremes
    ? runs(sorted.map((p) => (p.min !== null && p.max !== null ? 0 : null)))
        .filter((r) => r.length > 1)
        .map((r) => {
          const top = r.map((i) => `${x(i).toFixed(1)} ${y(sorted[i]!.max!).toFixed(1)}`);
          const bottom = [...r].reverse().map((i) => `${x(i).toFixed(1)} ${y(sorted[i]!.min!).toFixed(1)}`);
          return `M ${[...top, ...bottom].join(' L ')} Z`;
        })
        .join(' ')
    : '';

  const first = sorted[0]!;
  const last = sorted[sorted.length - 1]!;
  const caption = `${longDate(first.at)} to ${longDate(last.at)} · ${sorted.length === 1 ? '1 sighting' : `${sorted.length} sightings`}`;

  return (
    <figure className={className}>
      <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} className="h-auto w-full" preserveAspectRatio="xMidYMid meet" role="img" aria-label={`Market price for this part, ${caption}`}>
        <defs>
          <clipPath id={clipId}>
            <rect x={PAD.left} y={PAD.top} width={PLOT_W} height={PLOT_H} />
          </clipPath>
        </defs>

        {band && (
          <g clipPath={`url(#${clipId})`}>
            <path d={band} className="fill-slate-100" />
          </g>
        )}

        {ticks.map((v) => (
          <g key={`tick-${v}`}>
            <line x1={PAD.left} x2={PAD.left + PLOT_W} y1={y(v)} y2={y(v)} className="stroke-slate-200" strokeWidth={1} />
            <text x={PAD.left - 8} y={y(v)} textAnchor="end" dominantBaseline="middle" fontSize={11} className="fill-slate-500">
              {unitMoney(v, ccy)}
            </text>
          </g>
        ))}

        <g clipPath={`url(#${clipId})`}>
          {minRuns
            .filter((r) => r.length > 1)
            .map((r) => (
              <path key={`min-${r[0]}`} d={path(r, sorted.map((p) => p.min))} fill="none" className="stroke-slate-300" strokeWidth={1} strokeDasharray="2 3" />
            ))}
          {maxRuns
            .filter((r) => r.length > 1)
            .map((r) => (
              <path key={`max-${r[0]}`} d={path(r, sorted.map((p) => p.max))} fill="none" className="stroke-slate-300" strokeWidth={1} strokeDasharray="2 3" />
            ))}

          {hasQty &&
            qtyRuns.map((r) => (
              <path key={`qty-${r[0]}`} d={path(r, qtyAvgs)} fill="none" className="stroke-slate-400" strokeWidth={1.5} strokeDasharray="5 3" strokeLinecap="round" />
            ))}

          {avgRuns.map((r) => (
            <g key={`avg-${r[0]}`}>
              {r.length === 1 ? (
                <circle cx={x(r[0]!)} cy={y(avgs[r[0]!]!)} r={3.5} className="fill-slate-900" />
              ) : (
                <>
                  <path d={path(r, avgs)} fill="none" className="stroke-slate-900" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                  {r.map((i) => (
                    <circle key={i} cx={x(i)} cy={y(avgs[i]!)} r={2.5} className="fill-slate-900" />
                  ))}
                </>
              )}
            </g>
          ))}
        </g>

        <line x1={PAD.left} x2={PAD.left + PLOT_W} y1={PAD.top + PLOT_H} y2={PAD.top + PLOT_H} className="stroke-slate-300" strokeWidth={1} />

        {labels.map((l) => (
          <text
            key={l.i}
            x={x(l.i)}
            y={PAD.top + PLOT_H + 16}
            textAnchor={labels.length === 1 ? 'middle' : l.i === 0 ? 'start' : l.i === sorted.length - 1 ? 'end' : 'middle'}
            fontSize={11}
            className="fill-slate-500"
          >
            {l.text}
          </text>
        ))}
      </svg>
      <figcaption className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
        <span className="flex items-center gap-1.5">
          <svg width="16" height="8" viewBox="0 0 16 8" aria-hidden="true" className="shrink-0">
            <line x1="0" y1="4" x2="16" y2="4" className="stroke-slate-900" strokeWidth="2" strokeLinecap="round" />
          </svg>
          Average
        </span>
        {hasQty && (
          <span className="flex items-center gap-1.5">
            <svg width="16" height="8" viewBox="0 0 16 8" aria-hidden="true" className="shrink-0">
              <line x1="0" y1="4" x2="16" y2="4" className="stroke-slate-400" strokeWidth="1.5" strokeDasharray="5 3" strokeLinecap="round" />
            </svg>
            Average by quantity
          </span>
        )}
        {showExtremes && (
          <span className="flex items-center gap-1.5">
            <svg width="16" height="8" viewBox="0 0 16 8" aria-hidden="true" className="shrink-0">
              <line x1="0" y1="4" x2="16" y2="4" className="stroke-slate-300" strokeWidth="1" strokeDasharray="2 3" />
            </svg>
            Lowest and highest
          </span>
        )}
        <span>{caption}</span>
      </figcaption>
    </figure>
  );
}
